/**
 * Ranking (wave K §5.5, §5.6, §7.3, §10.3). SQL finds and scores (`bm25(search_fts, 5.0, 1.0)`: the title weighs 5×;
 * lower is better); this module orders and groups, so the Project DO, the web's fake server and tests agree.
 *
 * - Records: grouped by kind (item, decision, goal, kpi, output, brief, comment); inside a group settled first, greyed
 *   states (replaced, rejected, cancelled, expired) last within their settled band, then score, then newer.
 * - History: at most 2 hits per source (one run, or one chat day), best score first, ties newer first.
 * - ⌘K: exact ref, exact title, title prefix, word prefix, substring; ≤ 8 rows.
 */
import { type HitState, SEARCH_KIND_ORDER, type SearchHit, type SearchKind } from "@minga/protocol"
import { MARK_OPEN } from "./fts"

/** Hits per kind in a `search` answer by default (the tools' `limit`). */
export const SEARCH_LIMIT_DEFAULT = 5
export const SEARCH_LIMIT_MAX = 30
/** Chat hits in a `search` answer. */
export const CHAT_HITS_MAX = 3
/** `look_back` defaults. */
export const LOOK_BACK_LIMIT_DEFAULT = 8
export const LOOK_BACK_LIMIT_MAX = 20
export const LOOK_BACK_PER_SOURCE = 2
export const LOOK_BACK_WINDOW_DAYS = 365
/** Snippet caps. */
export const SNIPPET_MAX = 240
export const HISTORY_SNIPPET_MAX = 300
/** When only the title matched: the body's first characters stand in for the snippet. */
export const TITLE_ONLY_SNIPPET = 200
/** ⌘K rows. */
export const JUMP_MAX = 8

/** States shown greyed (sorted last within their settled band). */
export const GREYED_STATES: ReadonlySet<HitState> = new Set<HitState>([
  "replaced",
  "rejected",
  "cancelled",
  "expired",
])

/** A record hit as SQL returns it (spec §5.5): the hit's fields plus the bm25 `score` and the raw snippet. */
export interface SearchRow extends Omit<SearchHit, "snippet"> {
  /** `snippet(search_fts, 1, '«', '»', '…', 24)`. */
  readonly snip: string
  /** bm25: lower is better. */
  readonly score: number
  /** The doc's body (optional): used when only the title matched (the snippet has no mark). */
  readonly body?: string | null
}

const clip = (s: string, max: number): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s)

/** The snippet of a row: the SQL snippet, or the body's first 200 chars when the match is only in the title. */
export const snippetOf = (row: Pick<SearchRow, "snip" | "body">): string => {
  if (!row.snip.includes(MARK_OPEN) && typeof row.body === "string" && row.body.trim() !== "")
    return clip(row.body.trim().replace(/\s+/g, " "), TITLE_ONLY_SNIPPET)
  return clip(row.snip, SNIPPET_MAX)
}

/** The hit of a row (snippet resolved and capped). */
export const hitOfRow = (row: SearchRow): SearchHit => {
  const { snip: _snip, score: _score, body: _body, ...rest } = row
  return { ...rest, snippet: snippetOf(row) }
}

/** The order inside a group: settled first, greyed last within their band, then score, then newer. */
export const compareRows = (a: SearchRow, b: SearchRow): number =>
  Number(b.settled) - Number(a.settled) ||
  Number(GREYED_STATES.has(a.state)) - Number(GREYED_STATES.has(b.state)) ||
  a.score - b.score ||
  b.at - a.at

export interface SearchGroups {
  readonly groups: ReadonlyArray<{
    readonly kind: SearchKind
    readonly hits: ReadonlyArray<SearchHit>
    readonly more: number
  }>
  /** All hits across groups (shown or not). */
  readonly total: number
}

/** Group already ordered hits by kind in {@link SEARCH_KIND_ORDER}: `limit` per group, `more` = the rest. Empty groups are left out. */
export const groupHits = (
  hits: ReadonlyArray<SearchHit>,
  limit: number = SEARCH_LIMIT_DEFAULT,
): SearchGroups => {
  const by = new Map<SearchKind, SearchHit[]>()
  for (const h of hits) {
    const g = by.get(h.kind)
    if (g) g.push(h)
    else by.set(h.kind, [h])
  }
  const n = Math.max(1, Math.min(SEARCH_LIMIT_MAX, Math.floor(limit)))
  const groups = SEARCH_KIND_ORDER.flatMap((kind) => {
    const g = by.get(kind)
    return g ? [{ kind, hits: g.slice(0, n), more: Math.max(0, g.length - n) }] : []
  })
  return { groups, total: hits.length }
}

/** `O-14@v2` → 2 (an older version's anchor), else null. */
const versionOfAnchor = (anchor: string): number | null => {
  const m = /^O-[1-9]\d*@v([1-9]\d*)$/i.exec(anchor)
  return m ? Number(m[1]) : null
}

/**
 * One hit per output ref (decision 0065: one `O-n` per version chain). Output rows that share a ref fold into the
 * chain's head: the head's row (its anchor is the ref), found among the rows or through `headRow` (the index's doc
 * for the ref, when only older versions matched). The folded row keeps the best score, the head's snippet when the
 * head matched (else the best older version's, with its taint), and lists the older versions that matched in `also`.
 * Other rows pass through unchanged.
 */
export const foldVersions = (
  rows: ReadonlyArray<SearchRow>,
  headRow: (ref: string) => SearchRow | null,
): ReadonlyArray<SearchRow> => {
  const groups = new Map<string, SearchRow[]>()
  for (const r of rows) {
    if (r.kind !== "output" || r.ref === null) continue
    const k = r.ref.toUpperCase()
    const g = groups.get(k)
    if (g) g.push(r)
    else groups.set(k, [r])
  }
  const folded = new Map<string, SearchRow>()
  for (const [ref, g] of groups) {
    const matchedHead = g.find((r) => r.anchor.toUpperCase() === ref)
    const older = g.filter((r) => r !== matchedHead && versionOfAnchor(r.anchor) !== null)
    if (older.length === 0) continue
    const head = matchedHead ?? headRow(ref)
    if (!head) continue
    const best = older.reduce((a, b) => (b.score < a.score ? b : a))
    const also = older
      .map((r) => ({ anchor: r.anchor, version: versionOfAnchor(r.anchor) as number }))
      .sort((a, b) => a.version - b.version)
    folded.set(ref, {
      ...head,
      score: Math.min(best.score, matchedHead?.score ?? Number.POSITIVE_INFINITY),
      snip: matchedHead ? matchedHead.snip : best.snip,
      body: (matchedHead ? matchedHead.body : best.body) ?? null,
      // a snippet taken from an older version carries that version's taint (agents get it withheld)
      tainted: head.tainted || (!matchedHead && best.tainted),
      also,
    })
  }
  if (folded.size === 0) return rows
  const out: SearchRow[] = []
  const done = new Set<string>()
  for (const r of rows) {
    const k = r.kind === "output" && r.ref !== null ? r.ref.toUpperCase() : null
    const f = k === null ? undefined : folded.get(k)
    if (!f || k === null) {
      out.push(r)
      continue
    }
    if (done.has(k)) continue
    done.add(k)
    out.push(f)
  }
  return out
}

/** Order rows (see {@link compareRows}), resolve snippets and group them. */
export const rankHits = (
  rows: ReadonlyArray<SearchRow>,
  opts: { readonly limit?: number } = {},
): SearchGroups => groupHits([...rows].sort(compareRows).map(hitOfRow), opts.limit ?? SEARCH_LIMIT_DEFAULT)

/** Chat hits in a `search` answer: at most three, `more` = the rest of a (capped) count. */
export const collapseChat = <H>(
  hits: ReadonlyArray<H>,
  total: number,
  max: number = CHAT_HITS_MAX,
): { readonly hits: ReadonlyArray<H>; readonly more: number } => {
  const shown = hits.slice(0, max)
  return { hits: shown, more: Math.max(0, Math.max(total, hits.length) - shown.length) }
}

/** A history row as SQL returns it: what ranking needs. */
export interface HistoryRankRow {
  readonly kind: "chat" | "run"
  /** `chat:<chatId>` | `run:<runId>`. */
  readonly source: string
  readonly at: number
  /** bm25: lower is better. */
  readonly score: number
}

/** The bucket a history row counts against: one run, or one chat on one UTC day. */
export const historyBucket = (r: HistoryRankRow): string =>
  r.kind === "chat" ? `${r.source}@${new Date(r.at).toISOString().slice(0, 10)}` : r.source

/**
 * Rank history rows: best score first (ties newer first), at most `perSource` per {@link historyBucket}, then
 * `limit`. `more` = more rows passed the per-source filter than `limit`.
 */
export const rankHistory = <R extends HistoryRankRow>(
  rows: ReadonlyArray<R>,
  opts: { readonly limit?: number; readonly perSource?: number } = {},
): { readonly rows: ReadonlyArray<R>; readonly more: boolean } => {
  const limit = Math.max(1, Math.min(LOOK_BACK_LIMIT_MAX, Math.floor(opts.limit ?? LOOK_BACK_LIMIT_DEFAULT)))
  const perSource = Math.max(1, opts.perSource ?? LOOK_BACK_PER_SOURCE)
  const sorted = [...rows].sort((a, b) => a.score - b.score || b.at - a.at)
  const counts = new Map<string, number>()
  const kept: R[] = []
  for (const r of sorted) {
    const b = historyBucket(r)
    const n = counts.get(b) ?? 0
    if (n >= perSource) continue
    counts.set(b, n + 1)
    kept.push(r)
  }
  return { rows: kept.slice(0, limit), more: kept.length > limit }
}

// ---- jump --------------------------------------------------------------------------------------------------------

/** A query that looks like a ref ("dic-12", "P-3", "O-14"). */
export const REF_QUERY = /^[A-Za-z][A-Za-z0-9]{0,4}-\d+$/

/** The ref a query names, upper-cased, or null. */
export const refQuery = (q: string): string | null => {
  const t = q.trim()
  return REF_QUERY.test(t) ? t.toUpperCase() : null
}

/** A thing ⌘K can jump to (the browser builds these from the state and `DecidedIndex`). */
export interface JumpEntry {
  readonly key: string
  readonly kind: string
  readonly ref: string | null
  readonly title: string
  readonly status?: string
}

export type JumpMatch = "ref" | "title" | "title_prefix" | "word_prefix" | "substring"
const MATCH_ORDER: Record<JumpMatch, number> = {
  ref: 0,
  title: 1,
  title_prefix: 2,
  word_prefix: 3,
  substring: 4,
}

const norm = (s: string): string => s.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().trim()

/** How an entry matches a query (null = not at all). */
export const jumpMatchOf = (q: string, e: JumpEntry): JumpMatch | null => {
  const n = norm(q)
  if (n === "") return null
  if (e.ref !== null && norm(e.ref) === n) return "ref"
  const t = norm(e.title)
  if (t === n) return "title"
  if (t.startsWith(n)) return "title_prefix"
  if (t.split(/[^\p{L}\p{N}]+/u).some((w) => w !== "" && w.startsWith(n))) return "word_prefix"
  if (t.includes(n) || (e.ref !== null && norm(e.ref).startsWith(n))) return "substring"
  return null
}

/**
 * ⌘K rows over what the browser holds: exact ref, exact title, title prefix, word prefix, substring (a ref prefix
 * counts as substring); within a band in input order. At most `max` (8).
 */
export const jumpMatches = <E extends JumpEntry>(
  q: string,
  entries: ReadonlyArray<E>,
  max: number = JUMP_MAX,
): ReadonlyArray<E & { readonly match: JumpMatch }> => {
  const out: Array<E & { readonly match: JumpMatch; readonly i: number }> = []
  entries.forEach((e, i) => {
    const match = jumpMatchOf(q, e)
    if (match) out.push({ ...e, match, i })
  })
  out.sort((a, b) => MATCH_ORDER[a.match] - MATCH_ORDER[b.match] || a.i - b.i)
  return out
    .slice(0, Math.max(0, max))
    .map(({ i: _i, ...rest }) => rest as unknown as E & { readonly match: JumpMatch })
}
